import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from "react";

import type { UsageBucket } from "../../lib/usageAggregation";

/**
 * Hand-rolled SVG time-series marks for the usage dashboard. The dashboard
 * deliberately avoids a charting dependency: three small mark shapes over one
 * shared bucket axis cover it, and the bundle stays lean.
 */

const MARGIN = { top: 12, right: 16, bottom: 26, left: 46 } as const;
const PLOT_HEIGHT = 180;

interface Scale {
  width: number;
  plotWidth: number;
  marginLeft: number;
  x: (index: number) => number;
  y: (value: number) => number;
  yMax: number;
}

function useContainerWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next && next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (value <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

/** Left margin sized to the widest y tick so long labels never clip. */
function axisMarginLeft(
  yMax: number,
  format: (value: number) => string,
): number {
  const widest = Math.max(
    ...yTicks(yMax, format).map((tick) => tick.label.length),
  );
  return Math.min(Math.max(MARGIN.left, widest * 7 + 12), 96);
}

function buildScale(
  width: number,
  count: number,
  yMax: number,
  marginLeft: number,
): Scale {
  const plotWidth = Math.max(width - marginLeft - MARGIN.right, 40);
  const slot = count > 1 ? plotWidth / (count - 1) : plotWidth;
  return {
    width,
    plotWidth,
    marginLeft,
    x: (index) => marginLeft + (count > 1 ? index * slot : plotWidth / 2),
    y: (value) =>
      MARGIN.top + PLOT_HEIGHT - (yMax > 0 ? (value / yMax) * PLOT_HEIGHT : 0),
    yMax,
  };
}

/** Keep the centered tooltip's body inside the plot card. */
function clampTooltipLeft(x: number, width: number): number {
  return Math.min(Math.max(x, 76), Math.max(width - 76, 76));
}

/** Slot width for bar charts: even bands, bars capped at 24px. */
function bandGeometry(
  scale: Scale,
  count: number,
  seriesCount: number,
): { band: number; bar: number; gap: number; groupWidth: number } {
  const band = scale.plotWidth / Math.max(count, 1);
  const gap = 2;
  const available = Math.max(band - 2, seriesCount);
  const bar = Math.min(
    24,
    Math.max((available - gap * Math.max(seriesCount - 1, 0)) / seriesCount, 1),
  );
  return {
    band,
    bar,
    gap,
    groupWidth: bar * seriesCount + gap * Math.max(seriesCount - 1, 0),
  };
}

function xTickIndexes(count: number, width: number): number[] {
  if (count === 0) return [];
  const maxTicks = Math.max(Math.floor(width / 90), 2);
  const step = Math.max(Math.ceil(count / maxTicks), 1);
  const ticks: number[] = [];
  for (let index = 0; index < count; index += step) ticks.push(index);
  const last = count - 1;
  if (ticks[ticks.length - 1] !== last) ticks.push(last);
  return ticks;
}

function yTicks(yMax: number, format: (value: number) => string) {
  const values = [0, yMax / 2, yMax];
  return values.map((value) => ({ value, label: format(value) }));
}

interface TooltipState {
  index: number;
  left: number;
  top: number;
}

interface TooltipRow {
  key: string;
  swatch?: string;
  label: string;
  value: string;
}

function ChartShell({
  title,
  legend,
  children,
  tooltip,
  containerRef,
}: {
  title: string;
  legend?: ReactNode;
  children: ReactNode;
  tooltip: ReactNode;
  containerRef: React.RefObject<HTMLDivElement>;
}) {
  return (
    <figure className="usage-chart" role="group" aria-label={title}>
      <figcaption className="usage-chart__head">
        <span className="usage-chart__title">{title}</span>
        {legend}
      </figcaption>
      <div className="usage-chart__plot" ref={containerRef}>
        {children}
        {tooltip}
      </div>
    </figure>
  );
}

function Tooltip({
  state,
  heading,
  rows,
}: {
  state: TooltipState | null;
  heading: string;
  rows: TooltipRow[];
}) {
  if (!state) return null;
  return (
    <div
      className="usage-tooltip"
      role="status"
      style={{ left: state.left, top: state.top }}
    >
      <span className="usage-tooltip__heading">{heading}</span>
      {rows.map((row) => (
        <span className="usage-tooltip__row" key={row.key}>
          {row.swatch && (
            <span
              className="usage-tooltip__key"
              style={{ background: row.swatch }}
              aria-hidden="true"
            />
          )}
          <strong>{row.value}</strong>
          <span>{row.label}</span>
        </span>
      ))}
    </div>
  );
}

function useHover(
  count: number,
  scale: Scale,
): {
  hover: TooltipState | null;
  onPointerMove: (event: PointerEvent<SVGSVGElement>) => void;
  onPointerLeave: () => void;
  focusIndex: (index: number) => void;
  blur: () => void;
} {
  const [hover, setHover] = useState<TooltipState | null>(null);
  const place = (index: number) => {
    const clamped = Math.min(Math.max(index, 0), count - 1);
    setHover({
      index: clamped,
      left: clampTooltipLeft(scale.x(clamped), scale.width),
      top: MARGIN.top,
    });
  };
  return {
    hover,
    onPointerMove: (event) => {
      if (count === 0) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - bounds.left - scale.marginLeft;
      const slot = count > 1 ? scale.plotWidth / (count - 1) : scale.plotWidth;
      place(Math.round(x / slot));
    },
    onPointerLeave: () => setHover(null),
    focusIndex: place,
    blur: () => setHover(null),
  };
}

function Axes({
  scale,
  buckets,
  format,
  xPosition = scale.x,
}: {
  scale: Scale;
  buckets: readonly UsageBucket[];
  format: (value: number) => string;
  xPosition?: (index: number) => number;
}) {
  const bottom = MARGIN.top + PLOT_HEIGHT;
  return (
    <g className="usage-axes" aria-hidden="true">
      {yTicks(scale.yMax, format).map((tick) => (
        <g key={tick.value}>
          <line
            x1={scale.marginLeft}
            x2={scale.width - MARGIN.right}
            y1={scale.y(tick.value)}
            y2={scale.y(tick.value)}
            className="usage-gridline"
          />
          <text
            x={scale.marginLeft - 8}
            y={scale.y(tick.value)}
            className="usage-tick usage-tick--y"
          >
            {tick.label}
          </text>
        </g>
      ))}
      {xTickIndexes(buckets.length, scale.width).map((index) => (
        <text
          key={buckets[index]?.key ?? index}
          x={xPosition(index)}
          y={bottom + 16}
          className="usage-tick usage-tick--x"
        >
          {buckets[index]?.label}
        </text>
      ))}
    </g>
  );
}

function linePath(points: readonly { x: number; y: number | null }[]): string {
  let path = "";
  let pen = false;
  for (const point of points) {
    if (point.y === null) {
      pen = false;
      continue;
    }
    path += `${pen ? "L" : "M"}${point.x.toFixed(1)},${point.y.toFixed(1)}`;
    pen = true;
  }
  return path;
}

export interface LineSeries {
  key?: string;
  name: string;
  color: string;
  values: readonly (number | null)[];
  format: (value: number) => string;
}

export function UsageLineChart({
  title,
  buckets,
  series,
  yMax: yMaxOverride,
  yTickFormat,
  tooltipExtra,
}: {
  title: string;
  buckets: readonly UsageBucket[];
  series: readonly LineSeries[];
  yMax?: number;
  yTickFormat: (value: number) => string;
  tooltipExtra?: (bucket: UsageBucket) => TooltipRow[];
}) {
  const [containerRef, width] = useContainerWidth();
  const titleId = useId();
  const dataMax = Math.max(
    0,
    ...series.flatMap((line) =>
      line.values.filter((value): value is number => value !== null),
    ),
  );
  const yMax = yMaxOverride ?? niceCeiling(dataMax);
  const scale = buildScale(
    width,
    buckets.length,
    yMax,
    axisMarginLeft(yMax, yTickFormat),
  );
  const { hover, onPointerMove, onPointerLeave, focusIndex, blur } = useHover(
    buckets.length,
    scale,
  );
  const height = MARGIN.top + PLOT_HEIGHT + MARGIN.bottom;
  const hoverBucket = hover ? buckets[hover.index] : undefined;
  const legend =
    series.length > 1 ? (
      <span className="usage-legend">
        {series.map((line) => (
          <span className="usage-legend__item" key={line.key ?? line.name}>
            <span
              className="usage-legend__line"
              style={{ background: line.color }}
              aria-hidden="true"
            />
            {line.name}
          </span>
        ))}
      </span>
    ) : undefined;

  return (
    <ChartShell
      title={title}
      legend={legend}
      containerRef={containerRef}
      tooltip={
        <Tooltip
          state={hover}
          heading={hoverBucket?.label ?? ""}
          rows={
            hoverBucket
              ? [
                  ...series.map((line) => ({
                    key: line.key ?? line.name,
                    swatch: line.color,
                    label: line.name,
                    value:
                      line.values[hover?.index ?? 0] !== null &&
                      line.values[hover?.index ?? 0] !== undefined
                        ? line.format(line.values[hover?.index ?? 0] as number)
                        : "—",
                  })),
                  ...(tooltipExtra?.(hoverBucket) ?? []),
                ]
              : []
          }
        />
      }
    >
      <svg
        width={width}
        height={height}
        role="img"
        aria-labelledby={titleId}
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        onFocus={() => focusIndex(buckets.length - 1)}
        onBlur={blur}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") focusIndex((hover?.index ?? 0) - 1);
          if (event.key === "ArrowRight") focusIndex((hover?.index ?? 0) + 1);
        }}
      >
        <title id={titleId}>{title}</title>
        <Axes scale={scale} buckets={buckets} format={yTickFormat} />
        {hover && (
          <line
            className="usage-crosshair"
            x1={hover.left}
            x2={hover.left}
            y1={MARGIN.top}
            y2={MARGIN.top + PLOT_HEIGHT}
          />
        )}
        {series.map((line) => {
          const points = buckets.map((_, index) => ({
            x: scale.x(index),
            y:
              line.values[index] !== null && line.values[index] !== undefined
                ? scale.y(line.values[index] as number)
                : null,
          }));
          return (
            <g key={line.key ?? line.name}>
              <path
                d={linePath(points)}
                fill="none"
                stroke={line.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {points.map(
                (point, index) =>
                  point.y !== null &&
                  (hover?.index === index ||
                    buckets.length === 1 ||
                    (index === buckets.length - 1 &&
                      line.values[index] !== null)) && (
                    <circle
                      key={buckets[index]?.key ?? index}
                      cx={point.x}
                      cy={point.y}
                      r={4}
                      fill={line.color}
                      className="usage-marker"
                    />
                  ),
              )}
            </g>
          );
        })}
      </svg>
    </ChartShell>
  );
}

export interface BarSeries {
  key?: string;
  name: string;
  color: string;
  values: readonly (number | null)[];
  format: (value: number) => string;
}

export function UsageBarChart({
  title,
  buckets,
  series,
  yTickFormat,
  tooltipExtra,
}: {
  title: string;
  buckets: readonly UsageBucket[];
  series: readonly BarSeries[];
  yTickFormat: (value: number) => string;
  tooltipExtra?: (bucket: UsageBucket) => TooltipRow[];
}) {
  const [containerRef, width] = useContainerWidth();
  const titleId = useId();
  const yMax = niceCeiling(
    Math.max(
      0,
      ...series.flatMap((item) =>
        item.values.filter((value): value is number => value !== null),
      ),
    ),
  );
  const scale = buildScale(
    width,
    buckets.length,
    yMax,
    axisMarginLeft(yMax, yTickFormat),
  );
  const { band, bar, gap, groupWidth } = bandGeometry(
    scale,
    buckets.length,
    Math.max(series.length, 1),
  );
  const [hover, setHover] = useState<TooltipState | null>(null);
  const height = MARGIN.top + PLOT_HEIGHT + MARGIN.bottom;
  const bottom = MARGIN.top + PLOT_HEIGHT;
  const hoverBucket = hover ? buckets[hover.index] : undefined;
  const barX = (index: number, seriesIndex: number) =>
    scale.marginLeft +
    index * band +
    (band - groupWidth) / 2 +
    seriesIndex * (bar + gap);
  const bucketX = (index: number) => scale.marginLeft + index * band + band / 2;
  const legend =
    series.length > 1 ? (
      <span className="usage-legend">
        {series.map((item) => (
          <span className="usage-legend__item" key={item.key ?? item.name}>
            <span
              className="usage-legend__line"
              style={{ background: item.color }}
              aria-hidden="true"
            />
            {item.name}
          </span>
        ))}
      </span>
    ) : undefined;

  return (
    <ChartShell
      title={title}
      legend={legend}
      containerRef={containerRef}
      tooltip={
        <Tooltip
          state={hover}
          heading={hoverBucket?.label ?? ""}
          rows={
            hoverBucket
              ? [
                  ...series.map((item) => {
                    const value = item.values[hover?.index ?? 0];
                    return {
                      key: item.key ?? item.name,
                      swatch: item.color,
                      label: item.name,
                      value:
                        value === null || value === undefined
                          ? "—"
                          : item.format(value),
                    };
                  }),
                  ...(tooltipExtra?.(hoverBucket) ?? []),
                ]
              : []
          }
        />
      }
    >
      <svg
        width={width}
        height={height}
        role="img"
        aria-labelledby={titleId}
        tabIndex={0}
        onPointerLeave={() => setHover(null)}
        onBlur={() => setHover(null)}
      >
        <title id={titleId}>{title}</title>
        <Axes
          scale={scale}
          buckets={buckets}
          format={yTickFormat}
          xPosition={bucketX}
        />
        {buckets.map((bucket, index) => {
          return (
            <g key={bucket.key}>
              {/* Full-band hit target so small bars stay hoverable. */}
              <rect
                x={scale.marginLeft + index * band}
                y={MARGIN.top}
                width={band}
                height={PLOT_HEIGHT}
                fill="transparent"
                onPointerMove={() =>
                  setHover({
                    index,
                    left: clampTooltipLeft(bucketX(index), scale.width),
                    top: MARGIN.top,
                  })
                }
              />
              {series.map((item, seriesIndex) => {
                const value = item.values[index];
                if (value === null || value === undefined || value <= 0)
                  return null;
                const top = scale.y(value);
                const barHeight = Math.max(bottom - top, 2);
                return (
                  <path
                    key={item.key ?? item.name}
                    d={roundedTopBar(
                      barX(index, seriesIndex),
                      top,
                      bar,
                      bottom - top,
                    )}
                    fill={item.color}
                    opacity={hover && hover.index !== index ? 0.55 : 1}
                    pointerEvents="none"
                    data-bar-height={barHeight.toFixed(1)}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
    </ChartShell>
  );
}

/** Bar with a 4px rounded data-end and a square baseline. */
function roundedTopBar(
  x: number,
  y: number,
  barWidth: number,
  barHeight: number,
): string {
  const radius = Math.min(4, barWidth / 2, barHeight);
  const bottom = y + barHeight;
  return [
    `M${x.toFixed(1)},${bottom.toFixed(1)}`,
    `V${(y + radius).toFixed(1)}`,
    `Q${x.toFixed(1)},${y.toFixed(1)} ${(x + radius).toFixed(1)},${y.toFixed(1)}`,
    `H${(x + barWidth - radius).toFixed(1)}`,
    `Q${(x + barWidth).toFixed(1)},${y.toFixed(1)} ${(x + barWidth).toFixed(1)},${(y + radius).toFixed(1)}`,
    `V${bottom.toFixed(1)}`,
    "Z",
  ].join("");
}
